import { useState } from "react";
import { toast } from "sonner";
import "./admin.css";
import { usePartners } from "@/hooks/queries/usePartners";
import { usePartnerCodes } from "@/hooks/queries/usePartnerCodes";
import { usePartnerMutations } from "@/hooks/queries/usePartnerMutations";
import type { Partner } from "@/hooks/queries/usePartners";
import type { PartnerCode } from "@/hooks/queries/usePartnerCodes";

const BLANK_PARTNER: Omit<Partner, "id" | "created_at" | "updated_at"> = {
  slug: "",
  branding_title: "",
  legal_name: "",
  legal_id: null,
  contact_email: null,
  destination_url: null,
  is_active: true,
  notes: null,
};

const QR_BASE_URL = "https://app.meetmori.app/?ref=";

async function downloadQr(code: string) {
  try {
    const QRCode = await import("qrcode");
    const dataUrl = await QRCode.toDataURL(`${QR_BASE_URL}${code}`, {
      width: 512,
      margin: 2,
    });
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `qr-${code}.png`;
    a.click();
  } catch {
    toast.error("QR generation failed.");
  }
}

export default function PartnersAdmin() {
  const { partners, isLoading } = usePartners();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);

  const selected = partners.find((p) => p.id === selectedId) ?? null;

  const openCreate = () => {
    setEditingPartner(null);
    setShowPartnerForm(true);
  };

  const openEdit = (p: Partner) => {
    setEditingPartner(p);
    setShowPartnerForm(true);
  };

  const closeForm = () => {
    setShowPartnerForm(false);
    setEditingPartner(null);
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Partners</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage partners and their QR-coded referral codes for inbound attribution.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700"
        >
          + New partner
        </button>
      </div>

      {showPartnerForm && (
        <PartnerForm
          initial={editingPartner}
          onClose={closeForm}
        />
      )}

      <div className="flex gap-6">
        {/* Partners list */}
        <div className="w-80 shrink-0">
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              {partners.length === 0 && (
                <p className="text-sm text-gray-400 p-4">No partners yet.</p>
              )}
              {partners.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  className={`w-full flex items-start justify-between px-4 py-3 text-left border-b border-gray-100 last:border-b-0 transition-colors ${
                    selectedId === p.id
                      ? "bg-orange-50"
                      : "bg-white hover:bg-gray-50"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {p.branding_title}
                      </span>
                      {!p.is_active && (
                        <span className="inline-block rounded px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500">
                          inactive
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 font-mono truncate">{p.slug}</div>
                  </div>
                  {selectedId === p.id && (
                    <span className="text-orange-600 text-xs ml-2 shrink-0 mt-0.5">▶</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Partner detail */}
        {selected && (
          <div className="flex-1 min-w-0">
            <PartnerDetail
              partner={selected}
              onEdit={() => openEdit(selected)}
            />
          </div>
        )}

        {!selected && !isLoading && (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg">
            Select a partner to manage referral codes
          </div>
        )}
      </div>
    </div>
  );
}

// ── Partner form (create + edit) ──────────────────────────────────────────────

interface PartnerFormProps {
  initial: Partner | null;
  onClose: () => void;
}

function PartnerForm({ initial, onClose }: PartnerFormProps) {
  const isNew = !initial;
  const { createPartner, updatePartner } = usePartnerMutations();

  const [draft, setDraft] = useState<Omit<Partner, "id" | "created_at" | "updated_at">>(
    initial
      ? {
          slug: initial.slug,
          branding_title: initial.branding_title,
          legal_name: initial.legal_name,
          legal_id: initial.legal_id,
          contact_email: initial.contact_email,
          destination_url: initial.destination_url,
          is_active: initial.is_active,
          notes: initial.notes,
        }
      : { ...BLANK_PARTNER }
  );

  const set = <K extends keyof typeof draft>(k: K, v: typeof draft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!draft.branding_title.trim()) {
      toast.error("Branding title is required.");
      return;
    }
    if (!draft.legal_name.trim()) {
      toast.error("Legal name is required.");
      return;
    }
    if (!draft.slug.trim()) {
      toast.error("Slug is required.");
      return;
    }
    if (!/^[a-z0-9-]+$/.test(draft.slug)) {
      toast.error("Slug must be lowercase letters, numbers, and hyphens only.");
      return;
    }

    const payload = {
      ...draft,
      slug: draft.slug.trim(),
      branding_title: draft.branding_title.trim(),
      legal_name: draft.legal_name.trim(),
      legal_id: draft.legal_id?.trim() || null,
      contact_email: draft.contact_email?.trim() || null,
      destination_url: draft.destination_url?.trim() || null,
      notes: draft.notes?.trim() || null,
    };

    if (isNew) {
      await createPartner.mutateAsync(payload);
    } else {
      await updatePartner.mutateAsync({ id: initial!.id, ...payload });
    }
    onClose();
  };

  const saving = createPartner.isPending || updatePartner.isPending;

  return (
    <div className="mb-8 rounded-lg border border-orange-200 bg-orange-50/50 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">
          {isNew ? "New partner" : `Edit: ${initial!.branding_title}`}
        </h2>
        <button onClick={onClose} className="text-xs text-gray-500 hover:text-gray-700">
          ✕ Close
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Branding title</label>
          <input
            type="text"
            value={draft.branding_title}
            onChange={(e) => {
              set("branding_title", e.target.value);
              if (isNew) {
                set(
                  "slug",
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-|-$/g, "")
                );
              }
            }}
            placeholder="e.g. Roasters Coffee"
            className="admin-input"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Slug (URL-safe, unique)</label>
          <input
            type="text"
            value={draft.slug}
            onChange={(e) => set("slug", e.target.value.toLowerCase())}
            placeholder="e.g. roasters-coffee"
            className="admin-input font-mono"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Legal name</label>
          <input
            type="text"
            value={draft.legal_name}
            onChange={(e) => set("legal_name", e.target.value)}
            placeholder="Registered company name"
            className="admin-input"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Legal ID / reg. number</label>
          <input
            type="text"
            value={draft.legal_id ?? ""}
            onChange={(e) => set("legal_id", e.target.value || null)}
            placeholder="Optional"
            className="admin-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Contact email</label>
          <input
            type="email"
            value={draft.contact_email ?? ""}
            onChange={(e) => set("contact_email", e.target.value || null)}
            placeholder="Optional"
            className="admin-input"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Destination URL</label>
          <input
            type="url"
            value={draft.destination_url ?? ""}
            onChange={(e) => set("destination_url", e.target.value || null)}
            placeholder="https://…"
            className="admin-input"
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-xs text-gray-500 mb-1">Notes (internal)</label>
        <textarea
          value={draft.notes ?? ""}
          onChange={(e) => set("notes", e.target.value || null)}
          rows={2}
          placeholder="Contract details, contact history, etc."
          className="admin-input"
        />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <input
          type="checkbox"
          id="partner-active"
          checked={draft.is_active}
          onChange={(e) => set("is_active", e.target.checked)}
          className="rounded"
        />
        <label htmlFor="partner-active" className="text-sm text-gray-700">
          Active (visible for attribution and shown to users)
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 pt-4 border-t border-orange-200/70">
        <button
          onClick={onClose}
          className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={() => void save()}
          disabled={saving}
          className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : isNew ? "Create partner" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

// ── Partner detail + referral codes ──────────────────────────────────────────

interface PartnerDetailProps {
  partner: Partner;
  onEdit: () => void;
}

function PartnerDetail({ partner, onEdit }: PartnerDetailProps) {
  const { codes, isLoading } = usePartnerCodes(partner.id);
  const { createCode, updateCode } = usePartnerMutations();
  const [showCodeForm, setShowCodeForm] = useState(false);

  const nextCodeSuggestion = () => {
    const n = (codes.length + 1).toString().padStart(3, "0");
    return `${partner.slug}-${n}`;
  };

  return (
    <div>
      {/* Partner summary card */}
      <div className="rounded-lg border border-gray-200 bg-white p-5 mb-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900">{partner.branding_title}</h2>
              {!partner.is_active && (
                <span className="inline-block rounded px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500">
                  inactive
                </span>
              )}
            </div>
            <div className="text-xs font-mono text-gray-400 mt-0.5">{partner.slug}</div>
          </div>
          <button
            onClick={onEdit}
            className="text-xs text-orange-600 hover:underline"
          >
            Edit
          </button>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
          <div>
            <dt className="text-gray-400">Legal name</dt>
            <dd className="text-gray-700">{partner.legal_name}</dd>
          </div>
          {partner.legal_id && (
            <div>
              <dt className="text-gray-400">Legal ID</dt>
              <dd className="text-gray-700 font-mono">{partner.legal_id}</dd>
            </div>
          )}
          {partner.contact_email && (
            <div>
              <dt className="text-gray-400">Contact</dt>
              <dd className="text-gray-700">{partner.contact_email}</dd>
            </div>
          )}
          {partner.destination_url && (
            <div>
              <dt className="text-gray-400">URL</dt>
              <dd className="text-gray-700 truncate">{partner.destination_url}</dd>
            </div>
          )}
          {partner.notes && (
            <div className="col-span-2">
              <dt className="text-gray-400">Notes</dt>
              <dd className="text-gray-700 whitespace-pre-wrap">{partner.notes}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Referral codes section */}
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Referral codes</h3>
          <button
            onClick={() => setShowCodeForm(true)}
            className="rounded-md bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700"
          >
            + New code
          </button>
        </div>

        {showCodeForm && (
          <CodeForm
            partnerId={partner.id}
            defaultCode={nextCodeSuggestion()}
            onClose={() => setShowCodeForm(false)}
          />
        )}

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : codes.length === 0 ? (
          <p className="text-sm text-gray-400">No referral codes yet.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Label</th>
                <th>QR URL</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <CodeRow
                  key={c.id}
                  code={c}
                  onToggle={() =>
                    void updateCode.mutateAsync({
                      id: c.id,
                      partner_id: c.partner_id,
                      is_active: !c.is_active,
                    })
                  }
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Code form ─────────────────────────────────────────────────────────────────

interface CodeFormProps {
  partnerId: string;
  defaultCode: string;
  onClose: () => void;
}

function CodeForm({ partnerId, defaultCode, onClose }: CodeFormProps) {
  const { createCode } = usePartnerMutations();
  const [code, setCode] = useState(defaultCode);
  const [label, setLabel] = useState("");

  const save = async () => {
    if (!code.trim()) {
      toast.error("Code is required.");
      return;
    }
    if (!label.trim()) {
      toast.error("Label is required.");
      return;
    }
    await createCode.mutateAsync({
      partner_id: partnerId,
      code: code.trim(),
      label: label.trim(),
      is_active: true,
    });
    onClose();
  };

  return (
    <div className="mb-4 rounded-md border border-orange-200 bg-orange-50/40 p-4">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Code (URL-safe)</label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="admin-input font-mono"
            placeholder="e.g. roasters-001"
          />
          <p className="text-xs text-gray-400 mt-0.5">
            Scans to: {QR_BASE_URL}{code || "…"}
          </p>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Label (internal)</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="admin-input"
            placeholder="e.g. Store entrance poster"
          />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={onClose}
          className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={() => void save()}
          disabled={createCode.isPending}
          className="rounded-md bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700 disabled:opacity-50"
        >
          {createCode.isPending ? "Creating…" : "Create code"}
        </button>
      </div>
    </div>
  );
}

// ── Code row ──────────────────────────────────────────────────────────────────

interface CodeRowProps {
  code: PartnerCode;
  onToggle: () => void;
}

function CodeRow({ code, onToggle }: CodeRowProps) {
  const [generating, setGenerating] = useState(false);

  const handleDownloadQr = async () => {
    setGenerating(true);
    await downloadQr(code.code);
    setGenerating(false);
  };

  return (
    <tr>
      <td>
        <span className="font-mono text-xs text-gray-800">{code.code}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700">{code.label}</span>
      </td>
      <td>
        <span className="font-mono text-xs text-gray-400 truncate max-w-[200px] block">
          {QR_BASE_URL}{code.code}
        </span>
      </td>
      <td>
        <span
          className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
            code.is_active
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {code.is_active ? "active" : "inactive"}
        </span>
      </td>
      <td>
        <div className="flex items-center gap-3">
          <button
            onClick={() => void handleDownloadQr()}
            disabled={generating}
            className="text-xs text-orange-600 hover:underline disabled:opacity-50"
          >
            {generating ? "Generating…" : "Download QR"}
          </button>
          <button
            onClick={onToggle}
            className="text-xs text-gray-500 hover:underline"
          >
            {code.is_active ? "Deactivate" : "Activate"}
          </button>
        </div>
      </td>
    </tr>
  );
}
